import {
    SuggestionDataMap,
    useSuggestionsShallowSelector,
} from 'components/WorkspaceEditor/SuggestionStore'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { defaultSuggestionCardFilter } from '../constants'
import { setUpdatedCardPositions } from '../utils/TrackChangesAndSuggestionsUtils'

/**
 * Custom hook to track and update positions of suggestion cards in the UI.
 * It handles loading states, filtering, and debounced updates of card positions.
 *
 * @returns {Object} An object containing state and functions related to suggestion cards.
 */
export const useTrackChangeCards = () => {
    const {
        suggestionsData,
        isLoadingCards,
        suggestionCardFilter,
    } = useSuggestionsShallowSelector((s) => ({
        suggestionsData: s.suggestionData,
        isLoadingCards: s.isLoadingCards,
        suggestionCardFilter: s.suggestionCardFilter,
    }))
    const prevCardBottomRef = useRef(0)
    const rafRef = useRef<number | null>(null)

    // TODO: Need this when we implement the Inline changes PBI
    // const isNotDefaultSuggestionCardFilter = useMemo(
    //     () => suggestionCardFilter !== defaultSuggestionCardFilter,
    //     [suggestionCardFilter]
    // )

    //TODO: Remove this when we implement the Inline changes PBI
    const isNotDefaultSuggestionCardFilter = true
    const isUnresolvedSuggestionCardFilter = useMemo(
        () => suggestionCardFilter === defaultSuggestionCardFilter,
        [suggestionCardFilter]
    )
    const shouldShowLoadingCards = isLoadingCards

    const flatSuggestions = useMemo(
        () =>
            Object.values(suggestionsData || {})?.length > 0
                ? Object.values(suggestionsData || {}).flatMap(
                      (group) => group?.suggestions || []
                  )
                : [],
        [suggestionsData]
    )

    const flatSuggestionsRef = useRef(flatSuggestions)

    flatSuggestionsRef.current = flatSuggestions

    const updateCardPositions = useCallback(
        async (overrideSuggestions?: Record<string, SuggestionDataMap>) => {
            setUpdatedCardPositions({
                flatSuggestionsRef,
                prevCardBottomRef,
                rafRef,
                overrideSuggestions,
            })
        },
        []
    )

    const debouncedUpdateCardPositions = useMemo(
        () => debounce(updateCardPositions, 300),
        [updateCardPositions]
    )

    useEffect(() => {
        // Cleanup animation frame on unmount
        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
                rafRef.current = null
            }
        }
    }, [])

    return {
        shouldShowLoadingCards,
        isNotDefaultSuggestionCardFilter,
        debouncedUpdateCardPositions,
        suggestionsData,
        isUnresolvedSuggestionCardFilter,
    }
}
